"use client";

import { useEffect } from "react";
import { useLenis } from "@/components/providers/LenisProvider";

export function useScrollLock(locked: boolean) {
  const lenis = useLenis();

  /*
   * Stop Lenis separately.
   * This effect must not be combined with the body-lock effect because
   * Lenis can become available after the component has mounted.
   */
  useEffect(() => {
    if (locked) {
      lenis?.stop();
    } else {
      lenis?.start();
    }

    return () => {
      lenis?.start();
    };
  }, [locked, lenis]);

  /*
   * Lock the actual browser window.
   * position: fixed is more reliable than overflow:hidden alone,
   * especially on iOS and when Lenis is installed.
   */
  useEffect(() => {
    if (!locked) return;

    const html = document.documentElement;
    const body = document.body;

    const scrollY = window.scrollY;
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    const previous = {
      htmlOverflow: html.style.overflow,
      htmlOverscrollBehavior: html.style.overscrollBehavior,

      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyPaddingRight: body.style.paddingRight,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
    };

    // Lock the document.
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";

    // Avoid horizontal layout shift when the scrollbar disappears.
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      html.style.overflow = previous.htmlOverflow;
      html.style.overscrollBehavior = previous.htmlOverscrollBehavior;

      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;
      body.style.paddingRight = previous.bodyPaddingRight;
      body.style.overscrollBehavior = previous.bodyOverscrollBehavior;

      // Restore the original page position.
      window.scrollTo(0, scrollY);
    };
  }, [locked]);
}